
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useSecureUsername } from "@/hooks/useSecureUsername";
import { useSecureInput } from "@/hooks/useSecureInput";
import { AlertTriangle, CheckCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const UsernameSettings = () => {
  const { username: currentUsername, isLoading } = useUserProfile();
  const { updateUsername, isUpdating } = useSecureUsername();
  const [showSuccess, setShowSuccess] = useState(false);
  
  const {
    value: newUsername,
    error: usernameError,
    setValue: setNewUsername,
    validate,
    isValid
  } = useSecureInput(currentUsername || '', {
    maxLength: 30,
    required: true
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    if (newUsername === currentUsername) {
      return;
    }

    const success = await updateUsername(newUsername);
    if (success) {
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    }
  };

  const hasChanges = newUsername !== currentUsername && newUsername.trim() !== '';

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Username</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Username</CardTitle>
        <CardDescription>
          Your unique username. You can change this up to 3 times per 24 hours.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              type="text"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              placeholder="Enter your username"
              disabled={isUpdating}
              className={usernameError ? "border-red-500" : ""}
            />
            {usernameError && (
              <p className="text-sm text-red-500">{usernameError}</p>
            )}
            <p className="text-xs text-muted-foreground">
              3-30 characters, letters, numbers, underscores, and hyphens only
            </p>
          </div>

          {showSuccess && (
            <Alert className="border-green-500 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <AlertDescription className="text-green-700">
                Username updated successfully!
              </AlertDescription>
            </Alert>
          )}

          <div className="flex items-center gap-2 pt-2">
            <Button
              type="submit"
              disabled={!hasChanges || !isValid || isUpdating}
            >
              {isUpdating ? "Updating..." : "Update Username"}
            </Button>
            
            {hasChanges && (
              <Alert className="flex-1 border-amber-500 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <AlertDescription className="text-amber-700">
                  You have unsaved changes
                </AlertDescription>
              </Alert>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default UsernameSettings;
